import { useState, useMemo, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useLeads } from "@/hooks/useLeads";
import { useProfile } from "@/hooks/useProfile";
import { useCalendar } from "@/hooks/useCalendar";
import { useTeamRole } from "@/hooks/useTeamRole";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import { useCommunityBroadcasts } from "@/hooks/useCommunityBroadcasts";
import { can } from "@/lib/permissions";
import { DbLead, getScoreColor, STATUS_OPTIONS, getFitTier } from "@/lib/types";
import LeadDrawer from "./LeadDrawer";
import FitTierBadge from "@/components/FitTierBadge";
import ActivationChecklist from "@/components/ActivationChecklist";
import {
  Users, Flame, CalendarCheck, DollarSign, BedDouble, MessageSquare,
  Search, Mail, Eye, ArrowUpRight, ArrowDownRight, UserCheck, TrendingUp, UserPlus, Radio,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// Sample data — only shown when the user explicitly clicks "Load sample data"
// on the empty Dashboard. Never used as a silent fallback (that misled users
// into thinking fake "Margaret T." rows were real activity).
const SAMPLE_HOMECARE: Partial<DbLead>[] = [
  { id: "m1", name: "Margaret T.", zip_code: "22030", score: 92, urgency: 4, payer_source: "Private Pay", status: "new", acuity_notes: "Alzheimer's/Dementia", enriched: false, created_at: new Date().toISOString(), updated_at: new Date().toISOString(), user_id: "", hours_needed: 30, transcript: null, emergency_alerted_at: null, enrichment_data: null },
  { id: "m2", name: "Robert H.", zip_code: "20171", score: 88, urgency: 3, payer_source: "LTC Insurance", status: "contacted", acuity_notes: "Post-Op Recovery", enriched: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString(), user_id: "", hours_needed: 20, transcript: null, emergency_alerted_at: null, enrichment_data: null },
  { id: "m3", name: "Linda K.", zip_code: "22041", score: 65, urgency: 2, payer_source: "Medicaid Waiver", status: "booked", acuity_notes: "Companion Care", enriched: false, created_at: new Date().toISOString(), updated_at: new Date().toISOString(), user_id: "", hours_needed: 15, transcript: null, emergency_alerted_at: null, enrichment_data: null },
  { id: "m4", name: "James W.", zip_code: "20109", score: 45, urgency: 2, payer_source: "Private Pay", status: "new", acuity_notes: "Chronic Condition", enriched: false, created_at: new Date().toISOString(), updated_at: new Date().toISOString(), user_id: "", hours_needed: 10, transcript: null, emergency_alerted_at: null, enrichment_data: null },
  { id: "m5", name: "Carol M.", zip_code: "22003", score: 100, urgency: 5, payer_source: "Private Pay", status: "new", acuity_notes: "24-Hour Care", enriched: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString(), user_id: "", hours_needed: 40, transcript: null, emergency_alerted_at: null, enrichment_data: null },
];

const SAMPLE_DATA_KEY = "lge.dashboard.sample_data";

function getScoreTier(score: number) {
  if (score >= 85) return { label: score === 100 ? "Emergency" : "Hot", bg: "bg-hot/10 text-hot", border: "border-l-hot" };
  if (score >= 50) return { label: "Warm", bg: "bg-warning/10 text-warning", border: "border-l-warning" };
  return { label: "Cold", bg: "bg-muted text-muted-foreground", border: "border-l-muted-foreground" };
}

export default function Dashboard() {
  const { leads, loading, updateLead } = useLeads();
  const { profile } = useProfile();
  const { provider: calendarProvider } = useCalendar();
  const { teamRole, loading: roleLoading } = useTeamRole();
  const { members } = useTeamMembers();
  const { getBroadcastForLead } = useCommunityBroadcasts();
  const navigate = useNavigate();
  const [selectedLead, setSelectedLead] = useState<DbLead | null>(null);
  const [showSample, setShowSample] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SAMPLE_DATA_KEY) === "1";
  });

  function toggleSample(next: boolean) {
    setShowSample(next);
    if (typeof window !== "undefined") {
      if (next) window.localStorage.setItem(SAMPLE_DATA_KEY, "1");
      else window.localStorage.removeItem(SAMPLE_DATA_KEY);
    }
  }

  const seg = (profile as any)?.segment || "homecare";
  // Real leads always win. Sample data only renders when the user explicitly
  // opts in from the empty-state card (and it is clearly labeled in the UI).
  const isShowingSample = leads.length === 0 && showSample;
  const displayLeads = leads.length > 0
    ? leads
    : (isShowingSample ? (SAMPLE_HOMECARE as DbLead[]) : []);
  const recentLeads = displayLeads.slice(0, 5);

  // KPI calculations
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const oneDayMs = 24 * 60 * 60 * 1000;
  const thisMonthLeads = displayLeads.filter(l => new Date(l.created_at) >= monthStart);
  const hotCount = displayLeads.filter(l => l.score >= 85).length;
  const bookedCount = displayLeads.filter(l => l.status === "booked").length;

  // Real period-over-period trends — compare this calendar month to last
  // calendar month. When the prior period is empty we surface `null` so the
  // KPI card hides the arrow instead of showing a fake "+12%".
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthLeads = displayLeads.filter(l => {
    const d = new Date(l.created_at);
    return d >= lastMonthStart && d < monthStart;
  });

  function pctDelta(curr: number, prev: number): number | null {
    if (prev === 0) return curr === 0 ? null : null; // no baseline → hide
    return Math.round(((curr - prev) / prev) * 100);
  }

  const totalLeadsTrend = pctDelta(thisMonthLeads.length, lastMonthLeads.length);
  const hotLeadsTrend = pctDelta(
    thisMonthLeads.filter(l => l.score >= 85).length,
    lastMonthLeads.filter(l => l.score >= 85).length
  );
  const bookedTrend = pctDelta(
    thisMonthLeads.filter(l => l.status === "booked").length,
    lastMonthLeads.filter(l => l.status === "booked").length
  );
  const pipelineTrend = pctDelta(
    thisMonthLeads.reduce((s, l) => s + (l.hours_needed ?? 0), 0),
    lastMonthLeads.reduce((s, l) => s + (l.hours_needed ?? 0), 0)
  );

  const pipelineValue = seg === "grouphome"
    ? ((profile as any)?.bed_count || 6) * 4200
    : displayLeads
        .filter(l => l.status !== "lost" && getFitTier(l) !== "low_fit")
        .reduce((s, l) => s + (l.hours_needed ?? 0) * 25 * 4.33, 0);

  // Top low-fit leads worth referring to the community (no active broadcast yet).
  // Limited to 5 to keep the dashboard glanceable.
  const broadcastCandidates = displayLeads
    .filter(l =>
      getFitTier(l) === "low_fit" &&
      l.status !== "closed" &&
      l.status !== "lost" &&
      !getBroadcastForLead(l.id)
    )
    .slice(0, 5);

  const bedsAvailable = seg === "grouphome"
    ? Math.max(0, ((profile as any)?.bed_count || 6) - displayLeads.filter(l => l.status === "booked" || l.status === "closed").length)
    : 0;

  // Owner: staff active this week
  const staffActiveCount = members.filter(m => m.last_login && new Date(m.last_login) >= sevenDaysAgo).length;
  const totalStaff = members.length || 1;
  const allActive = staffActiveCount === members.length && members.length > 0;

  // Owner: hot lead response rate
  const hotLeadsList = displayLeads.filter(l => l.score >= 85);
  const respondedInTime = hotLeadsList.filter(l => {
    if (l.status === "new") return false;
    const created = new Date(l.created_at).getTime();
    const updated = new Date(l.updated_at).getTime();
    return updated - created <= oneDayMs;
  }).length;
  const responseRate = hotLeadsList.length > 0 ? Math.round((respondedInTime / hotLeadsList.length) * 100) : 0;

  // Manager: leads assigned this month
  const assignedThisMonth = thisMonthLeads.filter(l => (l as any).assigned_to).length;

  // Score distribution
  const hotLeads = displayLeads.filter(l => l.score >= 85).length;
  const warmLeads = displayLeads.filter(l => l.score >= 50 && l.score < 85).length;
  const coldLeads = displayLeads.filter(l => l.score < 50).length;
  const total = displayLeads.length || 1;

  // Status breakdown for donut
  const statusCounts = useMemo(() => {
    const counts = { new: 0, contacted: 0, booked: 0, closed: 0, lost: 0 };
    displayLeads.forEach(l => { if (counts[l.status as keyof typeof counts] !== undefined) counts[l.status as keyof typeof counts]++; });
    return counts;
  }, [displayLeads]);

  // Lead-source attribution — shows the owner where their leads are actually
  // coming from so they can see the ROI of the chat widget vs. manual entry
  // vs. community broadcasts. Empty/unknown sources are bucketed as "Other".
  const sourceCounts = useMemo(() => {
    const counts: Record<string, number> = {
      chat_widget: 0, manual: 0, community_broadcast: 0, api: 0, referral_import: 0, other: 0,
    };
    displayLeads.forEach(l => {
      const s = (l as any).source as string | null | undefined;
      if (s && counts[s] !== undefined) counts[s]++;
      else counts.other++;
    });
    return counts;
  }, [displayLeads]);

  const baseKpis = seg === "grouphome"
    ? [
        { icon: Users, label: "Total Inquiries", value: thisMonthLeads.length, color: "bg-primary/10 text-primary", trend: totalLeadsTrend, link: "/leads" },
        { icon: Flame, label: "Urgent Placements", value: hotCount, color: "bg-hot/10 text-hot", trend: hotLeadsTrend, link: "/leads" },
        { icon: BedDouble, label: "Beds Available", value: bedsAvailable, color: "bg-success/10 text-success", trend: null as number | null, link: "/leads" },
        { icon: DollarSign, label: "Revenue Potential", value: `$${pipelineValue.toLocaleString()}`, color: "bg-gold/10 text-gold", trend: pipelineTrend, link: "/leads" },
      ]
    : [
        { icon: Users, label: "Total Leads", value: thisMonthLeads.length, color: "bg-primary/10 text-primary", trend: totalLeadsTrend, link: "/leads" },
        { icon: Flame, label: "Hot Leads", value: hotCount, color: "bg-hot/10 text-hot", trend: hotLeadsTrend, link: "/leads" },
        { icon: CalendarCheck, label: "Calls Booked", value: bookedCount, color: "bg-success/10 text-success", trend: bookedTrend, link: "/leads" },
        { icon: DollarSign, label: "Pipeline Value", value: `$${Math.round(pipelineValue).toLocaleString()}`, color: "bg-gold/10 text-gold", trend: pipelineTrend, link: "/leads" },
      ];

  let kpis: typeof baseKpis = baseKpis;
  if (teamRole === "owner") {
    kpis = [
      ...baseKpis,
      { icon: UserCheck as any, label: "Staff Active This Week", value: `${staffActiveCount} / ${totalStaff}` as any, color: "bg-purple-100 text-purple-600", trend: null, link: "/team" },
      { icon: TrendingUp as any, label: seg === "grouphome" ? "Inquiry Response Rate" : "Lead Response Rate", value: `${responseRate}%` as any, color: "bg-teal-100 text-teal-600", trend: null, link: "/leads" },
    ];
  } else if (teamRole === "manager") {
    kpis = baseKpis.map(k =>
      k.label === "Pipeline Value" || k.label === "Revenue Potential"
        ? { icon: UserPlus as any, label: seg === "grouphome" ? "Inquiries Assigned" : "Leads Assigned", value: assignedThisMonth as any, color: "bg-blue-100 text-blue-600", trend: null, link: "/leads" }
        : k
    );
  }

  const quickActions = (seg === "grouphome"
    ? [
        { icon: MessageSquare, title: "Embed Intake Widget", desc: "Add your AI intake coordinator to your website.", link: "/embed", perm: "viewChatbotEmbed" as const },
        { icon: Search, title: "Find Coordinators", desc: "Search for support coordinators and MCO care managers.", link: "/referrals" },
        { icon: Mail, title: "Coordinator Outreach", desc: "Use personalized templates to reach support coordinators.", link: "/outreach" },
      ]
    : [
        { icon: MessageSquare, title: "Embed Your Chatbot", desc: "Add Sarah to your website and start capturing leads.", link: "/embed", perm: "viewChatbotEmbed" as const },
        { icon: Search, title: "Find Referral Partners", desc: "Search for discharge planners, therapists, and ALFs.", link: "/referrals" },
        { icon: Mail, title: "Send Outreach Emails", desc: "Use personalized templates to reach referral sources.", link: "/outreach" },
      ]
  ).filter(a => !("perm" in a) || can(teamRole as any, a.perm));

  // Staff: redirect to /tasks (after all hooks have run)
  if (!roleLoading && teamRole === "staff") {
    return <Navigate to="/tasks" replace />;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gold border-t-transparent" />
      </div>
    );
  }

  // Owner gets 6 cards (3 columns), others 4
  const gridCols = kpis.length === 6 ? "lg:grid-cols-3 xl:grid-cols-6" : "lg:grid-cols-4";

  // True-empty state — no real leads AND user has not opted into sample data.
  // We render a focused activation card instead of a fake-looking dashboard.
  if (leads.length === 0 && !showSample) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-dashed bg-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-gold/15 flex items-center justify-center">
            <Users className="h-6 w-6 text-gold" />
          </div>
          <h2 className="text-xl font-bold text-navy">
            {seg === "grouphome" ? "No inquiries yet" : "No leads yet"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
            {seg === "grouphome"
              ? "Embed your intake widget on your site and Sarah will start capturing family inquiries 24/7 — even tonight."
              : "Embed Sarah on your website and she'll start capturing family inquiries 24/7 — even tonight."}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            {can(teamRole as any, "viewChatbotEmbed") && (
              <Button onClick={() => navigate("/embed")} className="bg-gold text-gold-foreground hover:bg-gold/90">
                <MessageSquare className="h-4 w-4 mr-1.5" />
                Get my chat widget
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/quick-intake")}>
              Add a lead manually
            </Button>
            <Button variant="ghost" onClick={() => toggleSample(true)}>
              Preview with sample data
            </Button>
          </div>
        </div>

        {/* Quick actions still visible so the empty state is never a dead end */}
        <div className="grid gap-4 sm:grid-cols-3">
          {quickActions.map(a => (
            <div key={a.title} className="rounded-xl border bg-card p-5 shadow-sm flex flex-col">
              <a.icon className="h-8 w-8 text-gold mb-3" />
              <h3 className="font-bold text-navy text-[15px]">{a.title}</h3>
              <p className="text-sm text-muted-foreground mt-1 flex-1">{a.desc}</p>
              <button onClick={() => navigate(a.link)} className="text-sm font-medium text-gold mt-3 text-left hover:underline">
                Get started →
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sample-data banner — always visible while sample data is on so the
          user is never confused about whether the numbers are real. */}
      {isShowingSample && (
        <div className="rounded-lg border border-dashed border-gold bg-gold/5 px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-navy">
            <span className="font-semibold">Sample data preview</span>
            <span className="text-muted-foreground"> — these leads aren't real. Clear to see your live dashboard.</span>
          </div>
          <Button size="sm" variant="outline" onClick={() => toggleSample(false)}>
            Clear sample data
          </Button>
        </div>
      )}

      {/* Owner activation checklist — auto-hides once complete or dismissed.
          Only owners see it (managers/staff already have a focused view). */}
      {teamRole === "owner" && !isShowingSample && <ActivationChecklist />}

      {/* KPI Cards */}
      <div className={`grid grid-cols-2 gap-4 ${gridCols}`}>
        {kpis.map(k => (
          <button
            key={k.label}
            type="button"
            onClick={() => navigate(k.link)}
            className="rounded-xl border bg-card p-5 shadow-sm text-left w-full transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-gold/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${k.color}`}>
                <k.icon className="h-4 w-4" />
              </div>
              <span className="text-xs text-muted-foreground font-medium">{k.label}</span>
            </div>
            <div className="flex items-end justify-between">
              <span className="text-2xl font-bold text-navy">{k.value}</span>
              {k.trend !== null && k.trend !== 0 && (
                <span className={`flex items-center gap-0.5 text-xs font-medium ${k.trend > 0 ? "text-success" : "text-hot"}`}>
                  {k.trend > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {Math.abs(k.trend)}%
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 sm:grid-cols-3">
        {quickActions.map(a => (
          <div key={a.title} className="rounded-xl border bg-card p-5 shadow-sm flex flex-col">
            <a.icon className="h-8 w-8 text-gold mb-3" />
            <h3 className="font-bold text-navy text-[15px]">{a.title}</h3>
            <p className="text-sm text-muted-foreground mt-1 flex-1">{a.desc}</p>
            <button onClick={() => navigate(a.link)} className="text-sm font-medium text-gold mt-3 text-left hover:underline">
              Get started →
            </button>
          </div>
        ))}
      </div>

      {/* Leads worth broadcasting — surfaces low-fit leads so owners can refer
          them out instead of letting them rot in the pipeline. Hidden when
          there are none, so the dashboard stays clean. */}
      {broadcastCandidates.length > 0 && (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <div className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-primary" />
              <h3 className="font-bold text-navy">Leads worth broadcasting</h3>
              <Badge variant="secondary" className="text-xs">{broadcastCandidates.length}</Badge>
            </div>
            <span className="text-xs text-muted-foreground">Low fit for your agency — refer to the community</span>
          </div>
          <ul className="divide-y">
            {broadcastCandidates.map(lead => (
              <li
                key={lead.id}
                onClick={() => setSelectedLead(lead)}
                className="flex items-center gap-3 px-5 py-3 hover:bg-muted/30 cursor-pointer"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-navy truncate">{lead.name}</span>
                    <FitTierBadge tier="low_fit" />
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    {lead.zip_code || "—"} · {lead.acuity_notes || "No notes"} · {lead.payer_source || "—"}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={e => { e.stopPropagation(); setSelectedLead(lead); }}
                  className="gap-1.5 shrink-0"
                >
                  <Radio className="h-3.5 w-3.5" />
                  Refer
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent Leads */}
      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-bold text-navy">{seg === "grouphome" ? "Recent Inquiries" : "Recent Leads"}</h3>
          <button onClick={() => navigate("/leads")} className="text-sm font-medium text-gold hover:underline">View All →</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Name</th>
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{seg === "grouphome" ? "Locality" : "ZIP"}</th>
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Score</th>
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground hidden sm:table-cell">{seg === "grouphome" ? "Population" : "Specialty"}</th>
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground hidden sm:table-cell">{seg === "grouphome" ? "Waiver" : "Payer"}</th>
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground hidden md:table-cell">Date</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {recentLeads.map(lead => {
                const tier = getScoreTier(lead.score);
                return (
                  <tr
                    key={lead.id}
                    className={`border-b border-l-4 ${tier.border} hover:bg-muted/30 cursor-pointer transition-colors`}
                    onClick={() => setSelectedLead(lead)}
                  >
                    <td className="px-4 py-3 font-medium text-navy flex items-center gap-1.5">
                      {lead.score === 100 && <Flame className="h-4 w-4 text-gold animate-pulse" />}
                      {lead.name}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{lead.zip_code || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${tier.bg}`}>
                        {lead.score === 100 && <Flame className="h-3 w-3" />}
                        {lead.score}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{lead.acuity_notes || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{lead.payer_source || "—"}</td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={lead.status === "booked" ? "default" : "secondary"}
                        className="text-xs capitalize"
                      >
                        {lead.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={e => { e.stopPropagation(); setSelectedLead(lead); }}
                        className="p-1 rounded hover:bg-muted text-muted-foreground"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pipeline Summary */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Score Distribution */}
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <h3 className="font-bold text-navy text-[15px] mb-4">
            {seg === "grouphome" ? "Inquiry Score Distribution" : "Lead Score Distribution"}
          </h3>
          <div className="space-y-3">
            {[
              { label: "Hot (85–100)", count: hotLeads, color: "bg-hot" },
              { label: "Warm (50–84)", count: warmLeads, color: "bg-warning" },
              { label: "Cold (0–49)", count: coldLeads, color: "bg-muted-foreground" },
            ].map(b => (
              <div key={b.label} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-24 shrink-0">{b.label}</span>
                <div className="flex-1 h-7 bg-muted rounded-r-lg overflow-hidden">
                  <div className={`h-full ${b.color} rounded-r-lg transition-all duration-500`} style={{ width: `${(b.count / total) * 100}%` }} />
                </div>
                <span className="text-xs font-semibold text-navy w-8 text-right">{b.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Status Donut */}
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <h3 className="font-bold text-navy text-[15px] mb-4">
            {seg === "grouphome" ? "Inquiry Pipeline" : "Pipeline Status"}
          </h3>
          <div className="flex items-center gap-6">
            <DonutChart counts={statusCounts} total={displayLeads.length} />
            <div className="space-y-2">
              {[
                { label: "New", color: "bg-primary", count: statusCounts.new },
                { label: "Contacted", color: "bg-purple-500", count: statusCounts.contacted },
                { label: "Booked", color: "bg-success", count: statusCounts.booked },
                { label: "Closed", color: "bg-teal-500", count: statusCounts.closed },
                { label: "Lost", color: "bg-hot", count: statusCounts.lost },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-2">
                  <div className={`h-2.5 w-2.5 rounded-full ${s.color}`} />
                  <span className="text-xs text-muted-foreground">{s.label}</span>
                  <span className="text-xs font-semibold text-navy ml-auto">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lead source attribution — answers "where are my leads coming from?"
          which is the foundation of any ROI conversation. Hidden when there
          are no leads (the empty state already handles that case). */}
      {displayLeads.length > 0 && (
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <h3 className="font-bold text-navy text-[15px] mb-4">Where your leads come from</h3>
          <div className="space-y-2">
            {[
              { key: "chat_widget", label: "Chat widget", color: "bg-gold" },
              { key: "manual", label: "Manual entry", color: "bg-primary" },
              { key: "community_broadcast", label: "Community broadcast", color: "bg-success" },
              { key: "referral_import", label: "Referral import", color: "bg-purple-500" },
              { key: "api", label: "API", color: "bg-teal-500" },
              { key: "other", label: "Other / unknown", color: "bg-muted-foreground" },
            ]
              .filter(s => sourceCounts[s.key] > 0)
              .map(s => {
                const pct = Math.round((sourceCounts[s.key] / displayLeads.length) * 100);
                return (
                  <div key={s.key} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-36 shrink-0">{s.label}</span>
                    <div className="flex-1 h-6 bg-muted rounded-r-lg overflow-hidden">
                      <div className={`h-full ${s.color} rounded-r-lg transition-all duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-navy w-16 text-right">
                      {sourceCounts[s.key]} ({pct}%)
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <LeadDrawer
        lead={selectedLead}
        calendarProvider={calendarProvider}
        bookingLink={profile?.booking_link}
        onClose={() => setSelectedLead(null)}
        onUpdate={updateLead}
      />
    </div>
  );
}

function DonutChart({ counts, total }: { counts: Record<string, number>; total: number }) {
  const segments = [
    { key: "new", color: "#3b82f6" },
    { key: "contacted", color: "#a855f7" },
    { key: "booked", color: "#22c55e" },
    { key: "closed", color: "#14b8a6" },
    { key: "lost", color: "#ef4444" },
  ];
  const r = 40;
  const circ = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="relative shrink-0">
      <svg width="120" height="120" viewBox="0 0 100 100">
        {segments.map(s => {
          const pct = total > 0 ? (counts[s.key] || 0) / total : 0;
          const dash = pct * circ;
          const thisOffset = offset;
          offset += dash;
          if (pct === 0) return null;
          return (
            <circle
              key={s.key}
              cx="50" cy="50" r={r}
              fill="none" stroke={s.color} strokeWidth="12"
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-thisOffset}
              transform="rotate(-90 50 50)"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold text-navy">{total}</span>
        <span className="text-[10px] text-muted-foreground">Total</span>
      </div>
    </div>
  );
}
